import type { Snippet } from './types'
import { isCopyOfShared, isOutOfDate } from './utils'

export interface SnippetDraft {
  /** null while creating — a duplicate starts as a new snippet. */
  id: string | null
  /** The id a prompt token holds for this row (`Snippet.reference_id`). */
  referenceId?: string
  name: string
  content: string
  /**
   * The body as saved, never touched by editing. The Variants tab diffs
   * against this: diffing the live `content` would rewrite every row's diff
   * on each keystroke, and answer "what would my edit do" instead of "what
   * did this duty change".
   */
  savedContent?: string
  /** Duties referencing the saved snippet. 0 for a new one. */
  instanceCount: number
  /** Duties that have customised it. Absent until the count has resolved. */
  variantCount?: number
  /** `updated_at` as loaded, sent back as the save precondition. Null when new. */
  updatedAt: string | null
  /**
   * Set when the sheet is editing ONE duty's wording rather than the shared
   * snippet. `updatedAt` is that duty's row as loaded, and null while the fork
   * is still client-side — no row exists until the content diverges (KD16).
   */
  variant?: {
    dutyId: string
    sharedContent: string
    updatedAt: string | null
  } | null
  /** A Careforce snippet read from another org. Nothing here saves. */
  readOnly?: boolean
  /** Shown to a Careforce admin reading a customer org: where to edit instead. */
  managedElsewhere?: boolean
  /**
   * Creating this org's own copy of a Careforce snippet. `id` is null until
   * the first save creates the row; the source is what the copy follows.
   */
  customize?: { sourceId: string } | null
  /** This org's copy of a live Careforce snippet. */
  copy?: { sourceId: string; outOfDate: boolean } | null
  /** Careforce library: the row's sharing flag as loaded. */
  isShared?: boolean
  isHidden?: boolean
  /** Careforce global admin: may flip `isShared` and edit a shared row. */
  canShare?: boolean
  /** Careforce library, shared rows: the saved reach the sheet counts. */
  reach?: {
    orgCount: number
    dutyCount: number
    /** Orgs whose duties read the shared text, not a copy of it. Null until
     *  the relay sends it; the status line names only the duties then. */
    readingOrgCount: number | null
    otherOrgCount: number
    /** Null until the relay sends it; the status line estimates instead. */
    updatedDutyCount: number | null
  } | null
}

export function hidesFromOtherOrgs(draft: SnippetDraft | null): boolean {
  return Boolean(draft?.isHidden && (draft.isShared || draft.readOnly))
}

/** The fields every draft opened from a listed row shares. */
export function baseDraft(snippet: Snippet): SnippetDraft {
  return {
    id: snippet.id,
    referenceId: snippet.reference_id,
    name: snippet.name,
    content: snippet.content,
    savedContent: snippet.content,
    instanceCount: snippet.instance_count ?? 0,
    variantCount: snippet.override_count ?? 0,
    updatedAt: snippet.updated_at,
    isHidden: snippet.is_hidden ?? false,
  }
}

/** The draft for a row as the library shows it, in whichever mode it earns. */
export function draftFor(
  snippet: Snippet,
  {
    canShare,
    managedElsewhere,
  }: { canShare: boolean; managedElsewhere: boolean }
): SnippetDraft {
  const base = baseDraft(snippet)
  if (snippet.read_only) {
    return { ...base, readOnly: true, managedElsewhere }
  }
  if (isCopyOfShared(snippet)) {
    return {
      ...base,
      copy: {
        sourceId: snippet.source_snippet_id!,
        outOfDate: isOutOfDate(snippet),
      },
    }
  }
  return {
    ...base,
    isShared: snippet.is_shared ?? false,
    canShare,
    reach:
      snippet.is_shared && snippet.org_count != null
        ? {
            orgCount: snippet.org_count,
            dutyCount: snippet.duty_count_all_orgs ?? 0,
            readingOrgCount: snippet.blocking_org_count ?? null,
            otherOrgCount: snippet.other_org_count ?? 0,
            updatedDutyCount: snippet.updated_duty_count ?? null,
          }
        : null,
  }
}

/** Opens the Careforce text in an editor; the copy is created on first save. */
export function customizeDraftFor(snippet: Snippet): SnippetDraft {
  return {
    id: null,
    name: snippet.name,
    content: snippet.content,
    instanceCount: snippet.instance_count ?? 0,
    updatedAt: null,
    customize: { sourceId: snippet.id },
  }
}
